import { Box, CircularProgress, Container, Typography } from '@mui/material';
import { eachDayOfInterval, format, subWeeks, startOfWeek, endOfWeek } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PerformanceChart from '../components/charts/PerformanceChart';
import FiltersBar from '../components/FiltersBar.jsx';
import FaturamentoContratosTable from '../components/tables/FaturamentoContratosTable.jsx';
import TempoSaidaBaseRankingTable from '../components/tables/TempoSaidaBaseRankingTable';
import TurnosContratosTable from '../components/tables/TurnosContratosTable';
import TurnosOfensoresTable from '../components/tables/TurnosOfensoresTable';
import TempoPrimeiroServicoTable from '../components/tables/TempoPrimeiroServicoTable';
import { useMultipleQueries } from '../hooks/useApiCache';

const Performance = ({
  contrato: initialContrato = [],
  centro: initialCentro = [],
  equipe: initialEquipe = [],
  startDate: initialStartDate = null,
  endDate: initialEndDate = null,
  setContrato: parentSetContrato,
  setCentro: parentSetCentro,
  setEquipe: parentSetEquipe,
  setStartDate: parentSetStartDate,
  setEndDate: parentSetEndDate
}) => {
  // Calcular última semana como padrão
  const defaultLastWeek = useMemo(() => {
    const semanaAnterior = subWeeks(new Date(), 1);
    return {
      inicio: startOfWeek(semanaAnterior, { weekStartsOn: 0 }),
      fim: endOfWeek(semanaAnterior, { weekStartsOn: 0 })
    };
  }, []);

  // Estados dos filtros controlados localmente (pending)
  const [pendingContrato, setPendingContrato] = useState(initialContrato);
  const [pendingCentro, setPendingCentro] = useState(initialCentro);
  const [pendingEquipe, setPendingEquipe] = useState(initialEquipe);
  const [pendingStartDate, setPendingStartDate] = useState(initialStartDate || defaultLastWeek.inicio);
  const [pendingEndDate, setPendingEndDate] = useState(initialEndDate || defaultLastWeek.fim);

  // Estados dos filtros reais (usados para buscar dados)
  const [contrato, setContrato] = useState([]);
  const [centro, setCentro] = useState([]);
  const [equipe, setEquipe] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Controle de carregamento
  const [shouldLoadData, setShouldLoadData] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [loadCounter, setLoadCounter] = useState(0);
  const autoLoaded = useRef(false);

  // Handler do botão Carregar (definido antes do auto-load useEffect)
  const handleCarregar = useCallback(() => {
    setContrato(pendingContrato);
    setCentro(pendingCentro);
    setEquipe(pendingEquipe);
    setStartDate(pendingStartDate);
    setEndDate(pendingEndDate);
    setLoadCounter(prev => prev + 1);
    setShouldLoadData(true);
  }, [pendingContrato, pendingCentro, pendingEquipe, pendingStartDate, pendingEndDate]);

  // Auto-carregar quando todos os filtros forem preenchidos pela primeira vez
  useEffect(() => {
    if (pendingContrato.length > 0 && pendingCentro.length > 0 && pendingEquipe.length > 0 && pendingStartDate && pendingEndDate && !autoLoaded.current) {
      autoLoaded.current = true;
      handleCarregar();
    }
  }, [pendingContrato, pendingCentro, pendingEquipe, pendingStartDate, pendingEndDate, handleCarregar]);

  const isValidDate = (d) => d instanceof Date && !isNaN(d.getTime());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  let diasIntervalo = [];
  if (isValidDate(startDate) && isValidDate(endDate) && startDate <= endDate) {
    diasIntervalo = eachDayOfInterval({ start: startDate, end: endDate });
  }
  const diasLabels = diasIntervalo.map(date => format(date, 'dd/MM'));

  // Objeto de filtros reutilizável
  const filters = useMemo(() => ({ 
    startDate, 
    endDate, 
    contrato, 
    centro, 
    equipe
  }), [startDate, endDate, contrato, centro, equipe]);

  // Opções de query
  const queryOptions = {
    enabled: shouldLoadData,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false
  };

  // Usar useMultipleQueries para buscar todos os dados em paralelo
  const { data, isLoading } = useMultipleQueries([
    { endpoint: '/percentual-turnos-contrato', filters, options: { ...queryOptions, _key: loadCounter } },
    { endpoint: '/turnos-ofensores-contratos', filters, options: { ...queryOptions, _key: loadCounter } },
    { endpoint: '/tempo-medio-saida-base-contrato', filters, options: { ...queryOptions, _key: loadCounter } },
    { endpoint: '/producao-prevista-realizada-rev1-contrato', filters, options: { ...queryOptions, _key: loadCounter } },
    { endpoint: '/percentual-turnos-diario', filters, options: { ...queryOptions, _key: loadCounter } },
    { endpoint: '/tempo-primeiro-servico-contrato', filters, options: { ...queryOptions, _key: loadCounter } }
  ]);

  // Atualizar hasLoadedOnce quando terminar de carregar
  React.useEffect(() => {
    if (shouldLoadData && !isLoading) {
      setHasLoadedOnce(true);
      setShouldLoadData(false);
    }
  }, [shouldLoadData, isLoading]);

  // Desestruturar todos os dados
  const [
    turnosContratosData = [],
    turnosOfensoresData = [],
    tempoSaidaBaseData = [],
    faturamentoContratosData = [],
    turnosDiarioData = [],
    tempoPrimeiroServicoData = []
  ] = data;

  // Processar dados para gráfico
  const chartData = useMemo(() => {
    const rawData = Array.isArray(turnosDiarioData) ? turnosDiarioData : [];
    let dataMap = {};
    rawData.forEach(item => {
      if (item.data) dataMap[item.data] = item;
    });
    return diasIntervalo.map(date => {
      const key = format(date, 'yyyy-MM-dd');
      const item = dataMap[key];
      return {
        name: format(date, 'dd/MM'),
        meta: item ? Number(item.total_previsto) || 0 : 0,
        realizado: item ? Number(item.total_aberto) || 0 : 0
      };
    });
  }, [turnosDiarioData, diasIntervalo]);

  // Dados de turnos por contrato
  const turnosContratos = useMemo(() => {
    return Array.isArray(turnosContratosData) ? turnosContratosData : [];
  }, [turnosContratosData]);

  // Dados de turnos ofensores
  const turnosOfensores = useMemo(() => {
    return Array.isArray(turnosOfensoresData) ? turnosOfensoresData : [];
  }, [turnosOfensoresData]);

  // Dados de tempo saída base
  const tempoSaidaBaseContratos = useMemo(() => {
    return Array.isArray(tempoSaidaBaseData) ? tempoSaidaBaseData : [];
  }, [tempoSaidaBaseData]);

  // Dados de faturamento por contrato
  const faturamentoContratos = useMemo(() => {
    return Array.isArray(faturamentoContratosData) ? faturamentoContratosData : [];
  }, [faturamentoContratosData]);

  // Dados de tempo até primeiro serviço
  const tempoPrimeiroServico = useMemo(() => {
    return Array.isArray(tempoPrimeiroServicoData) ? tempoPrimeiroServicoData : [];
  }, [tempoPrimeiroServicoData]);

  // Dados de exemplo para o gráfico, alinhados com as datas
  const chartDataExample = diasLabels.map((label, idx) => ({
    name: label,
    tempo: [51.5, 67, 73.2, 60.8, 82.5, 42.2, 36][idx % 7],
    meta: 82.5
  }));

  return (
    <Container maxWidth="xl" sx={{ mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Performance
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Visualize e analise a performance das equipes filtrando por contrato, centro de serviço e período.
        </Typography>
      </Box>
      <FiltersBar
        contrato={pendingContrato}
        setContrato={setPendingContrato}
        centro={pendingCentro}
        setCentro={setPendingCentro}
        equipe={pendingEquipe}
        setEquipe={setPendingEquipe}
        startDate={pendingStartDate}
        setStartDate={setPendingStartDate}
        endDate={pendingEndDate}
        setEndDate={setPendingEndDate}
        onCarregar={handleCarregar}
        periodoSelecionado="ultima-semana"
        availablePeriods={['ultima-semana', 'ultimo-mes']}
      />
      {/* Nova tabela de turnos por contrato */}
      <Box sx={{ mt: 4 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TurnosContratosTable
            data={turnosContratos}
            startDate={startDate}
            endDate={endDate}
            contrato={contrato}
            centro={centro}
            equipe={equipe}
          />
        )}
      </Box>
      {/* Nova tabela de turnos ofensores */}
      <Box sx={{ mt: 4 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TurnosOfensoresTable 
            data={turnosOfensores} 
            startDate={startDate} 
            endDate={endDate} 
            contrato={contrato}
            centro={centro}
            equipe={equipe}
            loading={isLoading} 
          />
        )}
      </Box>
      {/* Ranking de Tempo Médio de Saída da Base */}
      <Box sx={{ mt: 4 }}>
        <TempoSaidaBaseRankingTable
          data={tempoSaidaBaseContratos}
          startDate={startDate}
          endDate={endDate}
          contrato={contrato}
          centro={centro}
          equipe={equipe}
          loading={isLoading}
        />
      </Box>

      {/* Ranking de Tempo até Primeiro Serviço */}
      <Box sx={{ mt: 4 }}>
        <TempoPrimeiroServicoTable
          data={tempoPrimeiroServico}
          loading={isLoading}
        />
      </Box>

      {/* Faturamento por Contrato */}
      <Box sx={{ mt: 4 }}>
        <FaturamentoContratosTable
          data={faturamentoContratos}
          loading={isLoading}
        />
      </Box>
      {/* Gráfico de área de exemplo */}
      <Box sx={{ mb: 4 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress />
          </Box>
        ) : chartData.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <Typography color="text.secondary">Sem dados para o período selecionado.</Typography>
          </Box>
        ) : (
          <PerformanceChart
            type="area"
            data={chartData}
            labels={diasLabels}
            exampleData={chartDataExample}
          />
        )}
      </Box>
    </Container>
  );
};

export default Performance;
